import { Navigate } from 'react-router-dom';
import { useFlag, useFeatureFlags } from '../hooks/useFeatureFlags';
import FeatureFlagsUnavailable from './FeatureFlagsUnavailable';
import SettingsBillingPage from '../pages/SettingsBillingPage';
import BillingPage from '../pages/BillingPage';

/**
 * Route element for `/settings/billing` — the cutover selector (task 630).
 *
 * The new itemized Billing page (`SettingsBillingPage`) takes the route whenever
 * `innovate.fe.settings-billing.page` is on (its baked default). Flipping that
 * flag OFF in Unleash is the rollback: the route then falls back to the legacy
 * `BillingPage` for as long as `innovate.fe.billing.page` is on. The legacy page
 * and its flag are removed in task 630's final retirement step once the new page
 * has proven out in the deployed environments.
 *
 * `/settings/billing` must stay a valid route in every flag combination — it is
 * the historical Stripe return URL (`?billing_return=1`). So this mirrors
 * `FlaggedRoute`'s ready / unleash-failed semantics rather than hard 404-ing:
 *   - flags not ready → render nothing (no premature redirect / no flash)
 *   - new flag on → the new page
 *   - new flag off, legacy flag on → the legacy page (rollback)
 *   - both off while the flag system failed → the stable unavailable panel
 *   - both off on a healthy source → redirect home (fail closed)
 */
export default function SettingsBillingRoute() {
  const { ready, source } = useFeatureFlags();
  const newPageEnabled = useFlag('innovate.fe.settings-billing.page');
  const legacyPageEnabled = useFlag('innovate.fe.billing.page');

  if (!ready) {
    return null;
  }

  if (newPageEnabled) {
    return <SettingsBillingPage />;
  }

  if (legacyPageEnabled) {
    return <BillingPage />;
  }

  if (source === 'unleash-failed') {
    return <FeatureFlagsUnavailable />;
  }

  return <Navigate to="/" replace />;
}
